import React from 'react';
import {
    type FieldPath,
    type FieldValues,
    type UseFormReturn,
    useController,
} from 'react-hook-form';

import { Icon as ContentIcon, type IconName } from '@gitbook/icons';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Group,
    Heading,
    IconPicker,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    usePopoverClose,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

/**
 * Icon picker field for selecting icon.
 */
export function IconPickerField<TFieldValues extends FieldValues>(props: {
    form: UseFormReturn<TFieldValues>;
    name: FieldPath<TFieldValues>;
    disabled?: boolean;
}) {
    const { form, name, disabled } = props;
    const { field } = useController({
        name,
        control: form.control,
        rules: {
            required: false,
            maxLength: 50,
            minLength: 1,
        },
        disabled,
    });
    const { value: icon, onChange } = field;
    return (
        <PopoverRoot>
            <PopoverTrigger>
                <Button
                    label={icon ? 'Change icon' : 'Add icon'}
                    iconOnly
                    kind="secondary"
                    leadingIcon={icon ? <ContentIcon icon={icon as IconName} /> : Icon.AddNew}
                    disabled={disabled}
                    aria-description={icon ? `Selected icon: ${icon}` : undefined}
                />
            </PopoverTrigger>
            <PickerPopover icon={icon} onChange={onChange} />
        </PopoverRoot>
    );
}

/**
 * Popover with icon picker for selecting section icon.
 */
function PickerPopover(props: { icon?: string | null; onChange: (icon: string | null) => void }) {
    const { icon, onChange } = props;
    const [query, setQuery] = React.useState('');
    const closePopover = usePopoverClose();

    const onPick = useEventCallback((value: string | null) => {
        onChange(value);
        closePopover();
    });

    return (
        <Popover className="flex-col p-2">
            <Group orientation="horizontal" className="min-h-8 w-full justify-between p-2">
                <Heading>Pick an icon</Heading>
                {icon ? (
                    <Button
                        onPress={() => onPick(null)}
                        label="Remove"
                        size="medium"
                        kind="blank"
                    />
                ) : null}
            </Group>
            <IconPicker onPick={(icon) => onPick(icon)} query={query} onChangeQuery={setQuery} />
        </Popover>
    );
}
